import { Reveal } from "./shared";
import { Terminal, GitBranch, Bell, CheckCircle2 } from "lucide-react";

const docCards = [
  { icon: Terminal, tag: "GET STARTED", title: "1-Click Deploy", desc: "Subscribe on Marketplace, pick your instance size, launch. SSH keys and endpoints ready in ~90 seconds." },
  { icon: GitBranch, tag: "CONFIG", title: "Environment", desc: "Everything driven by a single .env — SMTP, domains, retention. No opaque control planes." },
  { icon: Bell, tag: "INTEGRATE", title: "Webhooks & API", desc: "REST API + signed webhooks for Slack, PagerDuty and your own tooling." },
  { icon: CheckCircle2, tag: "OPERATE", title: "Backups & Upgrades", desc: "Snapshot-friendly volumes and zero-downtime rolling upgrades baked in." },
];

const workflows = [
  { step: "01", title: "Capture", body: "Tickets arrive via email, web form or API and land in a unified queue with auto-assigned IDs." },
  { step: "02", title: "Route", body: "Rules engine tags priority, assigns owners and starts SLA timers the moment a ticket opens." },
  { step: "03", title: "Resolve", body: "Threaded replies, internal notes and canned responses close the loop fast — fully audited." },
];

export const DocsWorkflows = () => (
  <>
    {/* DOCS */}
    <section id="docs" data-testid="docs-section" className="border-b border-white/10 py-24 md:py-40">
      <div className="mx-auto w-full max-w-screen-2xl px-6 md:px-12">
        <Reveal>
          <span className="label-mono">03 — Documentation</span>
          <h2 className="mt-4 max-w-2xl font-display text-3xl font-bold leading-none tracking-tight text-white md:text-5xl">
            Docs written for engineers, not marketers.
          </h2>
        </Reveal>

        <div className="mt-16 grid grid-cols-1 gap-6 sm:grid-cols-2 lg:grid-cols-4">
          {docCards.map((d, i) => {
            const Icon = d.icon;
            return (
              <Reveal key={d.title} delay={i * 0.08}>
                <div
                  data-testid={`doc-card-${i}`}
                  className="group flex h-full flex-col border border-white/10 bg-[#0A0A0A] p-6 transition-all duration-300 hover:-translate-y-1 hover:border-primary/60"
                >
                  <Icon className="h-5 w-5 text-primary" strokeWidth={1.5} />
                  <span className="mt-6 font-mono text-[10px] tracking-[0.2em] text-neutral-600">{d.tag}</span>
                  <h3 className="mt-1 font-display text-xl font-bold text-white">{d.title}</h3>
                  <p className="mt-2 font-sans text-sm leading-relaxed text-neutral-400">{d.desc}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>

    {/* WORKFLOWS */}
    <section id="workflows" data-testid="workflows-section" className="border-b border-white/10 py-24 md:py-40">
      <div className="mx-auto grid w-full max-w-screen-2xl grid-cols-1 gap-12 px-6 md:grid-cols-12 md:px-12">
        <div className="md:col-span-5">
          <div className="md:sticky md:top-28">
            <Reveal>
              <span className="label-mono">04 — Support Workflows</span>
              <h2 className="mt-4 font-display text-3xl font-bold leading-none tracking-tight text-white md:text-5xl">
                From inbound noise to closed ticket.
              </h2>
              <p className="mt-6 max-w-md font-sans text-base leading-relaxed text-neutral-400">
                Every ticket flows through a predictable, auditable lifecycle. No black boxes — you can read the
                source of every automation running inside your instance.
              </p>
              <div className="mt-8 border border-white/10 bg-black p-5 font-mono text-xs leading-relaxed text-neutral-400">
                <span className="text-primary">$</span> curl -X POST https://helpdesk.internal/api/tickets \<br />
                <span className="pl-4 text-neutral-600">-H "Authorization: Bearer $TOKEN" \</span><br />
                <span className="pl-4 text-neutral-600">-d '{"{"}"subject":"Login failing","priority":"high"{"}"}'</span><br />
                <span className="mt-2 block text-emerald-400">→ 201 Created · #TK-4821 · assigned</span>
              </div>
            </Reveal>
          </div>
        </div>

        <div className="flex flex-col gap-6 md:col-span-7">
          {workflows.map((w, i) => (
            <Reveal key={w.step} delay={i * 0.1}>
              <div
                data-testid={`workflow-step-${w.step}`}
                className="group flex gap-6 border border-white/10 bg-[#0A0A0A] p-8 transition-colors duration-300 hover:border-primary/60"
              >
                <span className="font-mono text-3xl font-bold text-primary">{w.step}</span>
                <div>
                  <h3 className="font-display text-2xl font-bold text-white">{w.title}</h3>
                  <p className="mt-2 font-sans text-sm leading-relaxed text-neutral-400">{w.body}</p>
                </div>
              </div>
            </Reveal>
          ))}
        </div>
      </div>
    </section>
  </>
);
